<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import InternalChatChannelsAPI from 'dashboard/api/internalChatChannels';
import MessageList from 'dashboard/routes/dashboard/internalChat/MessageList.vue';
import MessageEditor from 'dashboard/routes/dashboard/internalChat/MessageEditor.vue';
import TypingIndicator from 'dashboard/routes/dashboard/internalChat/TypingIndicator.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { isDirectMessageChannel, getChannelDisplayName } from './channelDisplay';
import { closeInternalChatPanel } from './sidepanelUi';

const props = defineProps({
  channelId: {
    type: Number,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const { updateUISettings } = useUISettings();

const editorRef = ref(null);
const messageListRef = ref(null);
const editingMessage = ref(null);
const isLoadingMore = ref(false);
const isViewingHistory = ref(false);
const firstUnreadMessageId = ref(null);
let typingOffTimer = null;

const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');

const channel = computed(() =>
  store.getters['internalChat/getChannelById'](props.channelId)
);

const messages = computed(() =>
  store.getters['internalChat/messages/getMessages'](props.channelId)
);

const messagesUIFlags = computed(
  () => store.getters['internalChat/messages/getUIFlags']
);

const currentUserId = computed(() => currentUser.value?.id);
const isAdmin = computed(() => currentRole.value === 'administrator');
const isArchived = computed(() => channel.value?.status === 'archived');

const typingUsers = computed(
  () =>
    store.getters['internalChatTypingStatus/getUserList'](props.channelId) || []
);

const threadDraftParentIds = computed(() =>
  store.getters['internalChat/drafts/getThreadDraftParentIds'](props.channelId)
);

function markRead() {
  store.dispatch('internalChat/markRead', props.channelId);
}

async function fetchMessages() {
  try {
    await store.dispatch('internalChat/messages/fetchMessages', {
      channelId: props.channelId,
    });
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.FETCH_MESSAGES'));
  }
}

function computeFirstUnreadId(unreadCount) {
  if (unreadCount > 0 && messages.value.length > 0) {
    const idx = Math.max(0, messages.value.length - unreadCount);
    firstUnreadMessageId.value = messages.value[idx]?.id || null;
  } else {
    firstUnreadMessageId.value = null;
  }
}

function loadDraft() {
  const draft = store.getters['internalChat/drafts/getDraftByChannelId'](
    props.channelId
  );
  if (editorRef.value) {
    editorRef.value.setContent(draft ? draft.content : '');
  }
}

function deleteDraftForChannel() {
  const draft = store.getters['internalChat/drafts/getDraftByChannelId'](
    props.channelId
  );
  if (draft) {
    store
      .dispatch('internalChat/drafts/deleteDraft', {
        channelId: props.channelId,
        draftId: draft.id,
      })
      .catch(() => {});
  }
}

function saveDraftImmediately() {
  const content = editorRef.value?.getContent?.() || '';
  if (content.trim()) {
    store
      .dispatch('internalChat/drafts/saveDraft', {
        channelId: props.channelId,
        content,
      })
      .catch(() => {});
  } else {
    deleteDraftForChannel();
  }
}

async function handleSend(content, options = {}) {
  try {
    if (editingMessage.value) {
      await store.dispatch('internalChat/messages/updateMessage', {
        channelId: props.channelId,
        messageId: editingMessage.value.id,
        data: { content },
      });
      editingMessage.value = null;
    } else {
      await store.dispatch('internalChat/messages/sendMessage', {
        channelId: props.channelId,
        data: { content },
        files: options.files || [],
      });
      markRead();
      deleteDraftForChannel();
    }
    editorRef.value?.clearAfterSend?.();
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.SEND_MESSAGE'));
  }
}

function handleEdit(message) {
  editingMessage.value = message;
}

function handleCancelEdit() {
  editingMessage.value = null;
}

async function handleDelete(message) {
  try {
    await store.dispatch('internalChat/messages/deleteMessage', {
      channelId: props.channelId,
      messageId: message.id,
    });
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.SEND_MESSAGE'));
  }
}

async function handleAddReaction({ messageId, emoji }) {
  try {
    await store.dispatch('internalChat/messages/addReaction', {
      channelId: props.channelId,
      messageId,
      emoji,
    });
  } catch {
    // ignore reaction errors
  }
}

async function handleRemoveReaction({ messageId, reactionId }) {
  try {
    await store.dispatch('internalChat/messages/removeReaction', {
      channelId: props.channelId,
      messageId,
      reactionId,
    });
  } catch {
    // ignore reaction errors
  }
}

function stopTyping(channelId = props.channelId) {
  if (typingOffTimer) {
    clearTimeout(typingOffTimer);
    typingOffTimer = null;
  }
  if (channelId) {
    InternalChatChannelsAPI.toggleTypingStatus(channelId, 'off');
  }
}

function handleTyping() {
  InternalChatChannelsAPI.toggleTypingStatus(props.channelId, 'on');
  if (typingOffTimer) clearTimeout(typingOffTimer);
  typingOffTimer = setTimeout(() => {
    InternalChatChannelsAPI.toggleTypingStatus(props.channelId, 'off');
  }, 3000);
}

async function handleDraftUpdate(content) {
  if (!content || !content.trim()) {
    deleteDraftForChannel();
    return;
  }
  try {
    await store.dispatch('internalChat/drafts/saveDraft', {
      channelId: props.channelId,
      content,
    });
  } catch {
    // ignore draft errors
  }
}

async function handlePin(message) {
  try {
    await store.dispatch('internalChat/messages/pinMessage', {
      channelId: props.channelId,
      messageId: message.id,
    });
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.SEND_MESSAGE'));
  }
}

async function handleUnpin(message) {
  try {
    await store.dispatch('internalChat/messages/unpinMessage', {
      channelId: props.channelId,
      messageId: message.id,
    });
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.SEND_MESSAGE'));
  }
}

async function handleVote({ messageId, optionId }) {
  const msg = store.getters['internalChat/messages/getMessageById'](
    props.channelId,
    messageId
  );
  const pollId = msg?.poll?.id || msg?.content_attributes?.poll?.id;
  if (!pollId) return;
  try {
    await store.dispatch('internalChat/polls/vote', {
      pollId,
      optionId,
      channelId: props.channelId,
    });
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.SEND_MESSAGE'));
  }
}

async function handleUnvote({ messageId, optionId }) {
  const msg = store.getters['internalChat/messages/getMessageById'](
    props.channelId,
    messageId
  );
  const pollId = msg?.poll?.id || msg?.content_attributes?.poll?.id;
  if (!pollId) return;
  try {
    await store.dispatch('internalChat/polls/unvote', {
      pollId,
      optionId,
      channelId: props.channelId,
    });
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.SEND_MESSAGE'));
  }
}

async function handleLoadMore() {
  if (!messages.value.length) return;
  const oldestMessage = messages.value[0];
  isLoadingMore.value = true;
  try {
    await store.dispatch('internalChat/messages/fetchMessages', {
      channelId: props.channelId,
      params: { before: oldestMessage.created_at },
    });
  } catch {
    // ignore pagination errors
  } finally {
    isLoadingMore.value = false;
  }
}

async function handleLoadNewer() {
  if (!messages.value.length) return;
  const newestMessage = messages.value[messages.value.length - 1];
  try {
    const result = await store.dispatch('internalChat/messages/fetchMessages', {
      channelId: props.channelId,
      params: { after: newestMessage.created_at },
    });
    if (!result || result.length === 0) {
      isViewingHistory.value = false;
    }
  } catch {
    // ignore
  }
}

function handleJumpToLatest() {
  isViewingHistory.value = false;
  fetchMessages();
}

function openThreadInFullApp(message) {
  const parentId = message.parent_id || message.id;
  const routeName = isDirectMessageChannel(channel.value)
    ? 'internal_chat_dm'
    : 'internal_chat_channel';

  closeInternalChatPanel(updateUISettings);
  router.push({
    name: routeName,
    params: {
      accountId: accountId.value,
      channelId: props.channelId,
    },
    query: {
      parentId,
      messageId: message.id,
    },
  });
}

async function activateChannel() {
  store.dispatch('internalChat/setActiveChannel', props.channelId);
  editingMessage.value = null;
  isViewingHistory.value = false;
  firstUnreadMessageId.value = null;
  const draftsPromise = store
    .dispatch('internalChat/drafts/fetchDrafts')
    .catch(() => {});
  const unreadCount = channel.value?.unread_count || 0;
  await fetchMessages();
  computeFirstUnreadId(unreadCount);
  markRead();
  await draftsPromise;
  await nextTick();
  loadDraft();
}

watch(
  () => props.channelId,
  async (_newId, oldId) => {
    if (oldId) {
      stopTyping(oldId);
      saveDraftImmediately();
    }
    await activateChannel();
  }
);

onMounted(async () => {
  await activateChannel();
});

onBeforeUnmount(() => {
  stopTyping();
  saveDraftImmediately();
  if (store.getters['internalChat/getActiveChannelId'] === props.channelId) {
    store.dispatch('internalChat/setActiveChannel', null);
  }
});
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-n-solid-1">
    <MessageList
      ref="messageListRef"
      class="min-h-0 flex-1"
      :channel-id="channelId"
      :messages="messages"
      :current-user-id="currentUserId"
      :is-admin="isAdmin"
      :is-loading="messagesUIFlags.isFetching"
      :is-loading-more="isLoadingMore"
      :is-viewing-history="isViewingHistory"
      :first-unread-message-id="firstUnreadMessageId"
      :thread-draft-parent-ids="threadDraftParentIds"
      @edit="handleEdit"
      @delete="handleDelete"
      @reply="openThreadInFullApp"
      @open-thread="openThreadInFullApp"
      @add-reaction="handleAddReaction"
      @remove-reaction="handleRemoveReaction"
      @pin="handlePin"
      @unpin="handleUnpin"
      @vote="handleVote"
      @unvote="handleUnvote"
      @load-more="handleLoadMore"
      @load-newer="handleLoadNewer"
      @jump-to-latest="handleJumpToLatest"
    />

    <TypingIndicator :typing-users="typingUsers" />

    <MessageEditor
      v-if="!isArchived"
      ref="editorRef"
      compact
      :disabled="messagesUIFlags.isSending"
      :editing-message="editingMessage"
      :show-poll="false"
      :placeholder="
        isDirectMessageChannel(channel)
          ? t('INTERNAL_CHAT.MESSAGE.PLACEHOLDER_TO', {
              name: getChannelDisplayName(channel, currentUserId),
            })
          : t('INTERNAL_CHAT.MESSAGE.PLACEHOLDER')
      "
      @send="handleSend"
      @typing="handleTyping"
      @draft-update="handleDraftUpdate"
      @cancel-edit="handleCancelEdit"
    />
    <div
      v-else
      class="border-t border-n-slate-5 bg-n-solid-2 px-4 py-3 text-center text-sm text-n-slate-10"
    >
      {{ t('INTERNAL_CHAT.CHANNEL.ARCHIVED') }}
    </div>
  </div>
</template>
